/**
 * The Orders screen lives in the URL.
 *
 * Every control on the page writes here and reads back from here: the search box, the
 * range chips, the queue rail, the filter popover and the sort. That is what makes a link
 * to a filtered view a real link, makes the browser Back button undo a filter rather than
 * leave the screen, and makes a page restored from history show what it showed before.
 *
 * Parsing is total. Anything the URL can carry is either a value this screen understands
 * or the default, because a hand-edited query string must not be able to blank the page.
 * Serialising drops every value that equals its default, so `/orders` stays `/orders`.
 */
import {
  OrderQueueSchema,
  OrderRangeSchema,
  PaymentMethodSchema,
  type OrderQueue,
  type OrderRange,
  type PaymentMethod,
} from '@elutide/shared';
import { STATUS_FILTERS } from './types';

export type OrdersSort = 'placed' | 'total' | 'customer' | 'state';
export type SortDir = 'asc' | 'desc';

export interface OrdersSearch {
  q: string;
  range: OrderRange;
  queue: OrderQueue;
  status: readonly string[];
  payment_method: readonly PaymentMethod[];
  first_order: boolean;
  sort: OrdersSort;
  dir: SortDir;
}

/**
 * What an empty URL means.
 *
 * `all` rather than a work queue, because the first thing this page is for is finding one
 * order by typing anything about it, and a rail that has already narrowed the set is a
 * search that quietly misses. The five pipeline tiles are one click away from here.
 */
export const DEFAULT_SEARCH: OrdersSearch = {
  q: '',
  range: '7d',
  queue: 'all',
  status: [],
  payment_method: [],
  first_order: false,
  sort: 'placed',
  dir: 'desc',
};

const SORTS: readonly OrdersSort[] = ['placed', 'total', 'customer', 'state'];
const STATUS_IDS: ReadonlySet<string> = new Set(STATUS_FILTERS.map((s) => s.id));

function str(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}

/** A repeated parameter and one comma separated value both mean a list. */
function list(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : [value];
  return raw
    .flatMap((entry) => str(entry).split(','))
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

/**
 * The router parses `?first_order=true` into a real boolean, and a hand typed URL or a
 * pasted link carries the string. Both have to mean the same thing.
 */
function bool(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (value === 1) return true;
  const text = str(value).toLowerCase();
  return text === 'true' || text === '1';
}

export function parseOrdersSearch(raw: Record<string, unknown> | undefined): OrdersSearch {
  const source = raw ?? {};
  const range = OrderRangeSchema.safeParse(str(source.range));
  const queue = OrderQueueSchema.safeParse(str(source.queue));
  const sortRaw = str(source.sort) as OrdersSort;
  const dirRaw = str(source.dir);

  return {
    // Trimmed on the way in, so a URL with a trailing space is the same URL.
    q: str(source.q).slice(0, 200).trim(),
    range: range.success ? range.data : DEFAULT_SEARCH.range,
    queue: queue.success ? queue.data : DEFAULT_SEARCH.queue,
    status: list(source.status).filter((id) => STATUS_IDS.has(id)),
    payment_method: list(source.payment_method).filter(
      (id): id is PaymentMethod => PaymentMethodSchema.safeParse(id).success,
    ),
    first_order: bool(source.first_order),
    sort: SORTS.includes(sortRaw) ? sortRaw : DEFAULT_SEARCH.sort,
    dir: dirRaw === 'asc' ? 'asc' : 'desc',
  };
}

/**
 * The search object to put in the URL. Defaults are omitted rather than spelled out, so
 * the address bar shows what was chosen and nothing else.
 */
export function toSearchParams(search: OrdersSearch): Record<string, string> {
  const params: Record<string, string> = {};
  if (search.q !== '') params.q = search.q;
  if (search.range !== DEFAULT_SEARCH.range) params.range = search.range;
  if (search.queue !== DEFAULT_SEARCH.queue) params.queue = search.queue;
  if (search.status.length > 0) params.status = [...search.status].join(',');
  if (search.payment_method.length > 0) {
    params.payment_method = [...search.payment_method].join(',');
  }
  if (search.first_order) params.first_order = 'true';
  if (search.sort !== DEFAULT_SEARCH.sort) params.sort = search.sort;
  if (search.dir !== DEFAULT_SEARCH.dir) params.dir = search.dir;
  return params;
}

/**
 * The query `GET /orders/page` is called with.
 *
 * Unlike the URL form this one is explicit about the range, the queue and the sort, so a
 * change to the client default can never silently change what the server was asked for.
 */
export function toPageQuery(
  search: OrdersSearch,
): Record<string, string | number | boolean | undefined> {
  return {
    q: search.q === '' ? undefined : search.q,
    range: search.range,
    queue: search.queue,
    status: search.status.length > 0 ? [...search.status].join(',') : undefined,
    payment_method:
      search.payment_method.length > 0 ? [...search.payment_method].join(',') : undefined,
    first_order: search.first_order ? true : undefined,
    sort: search.sort,
    dir: search.dir,
    limit: 100,
  };
}

/** How many long tail filters are on, for the badge on the Filter button. */
export function activeFilterCount(search: OrdersSearch): number {
  return search.status.length + search.payment_method.length + (search.first_order ? 1 : 0);
}

/** The search with every long tail filter cleared. The rail, range and query are kept. */
export function withoutFilters(search: OrdersSearch): OrdersSearch {
  return { ...search, status: [], payment_method: [], first_order: false };
}

/** Toggle one value in one of the two list filters, without touching the other. */
export function toggleInList<T extends string>(current: readonly T[], value: T): T[] {
  return current.includes(value)
    ? current.filter((entry) => entry !== value)
    : [...current, value];
}
